import React, {FC, useCallback, useEffect, useState} from 'react'
import {ToastContainer} from 'react-toastify'
import {PageTitle} from '../../../../_metronic/layout/core'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {createJob, deleteJob, loadJobs, updateJob} from './Api'
import {useHistory} from 'react-router-dom'
import {IJobFormValues, IJobModel, jobDashboardPath, publicJobUrl, toJobRequest} from './JobModel'
import {ShareJobModal} from './ShareJobModal'
import {JobTable} from './JobTable'
import {JobFormModal} from './JobFormModal'

const JobPage: FC = () => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [jobs, setJobs] = useState<IJobModel[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [showForm, setShowForm] = useState(false)
  const [editingItem, setEditingItem] = useState<IJobModel | null>(null)
  const [deletingItem, setDeletingItem] = useState<IJobModel | null>(null)
  const [sharingItem, setSharingItem] = useState<IJobModel | null>(null)
  const history = useHistory()

  const fetchJobs = useCallback(async (page: number) => {
    setLoading(true)
    try {
      const payload = (await loadJobs(page)).data.payload
      setJobs(payload.content)
      setTotalPages(payload.totalPages)
    } catch (error) {
      showErrorToast('İlanlar yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchJobs(0)
  }, [fetchJobs])

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchJobs(newPage)
      }
    },
    [totalPages, fetchJobs]
  )

  const handleCreateClick = () => {
    setEditingItem(null)
    setShowForm(true)
  }

  const handleEdit = useCallback((job: IJobModel) => {
    setEditingItem(job)
    setShowForm(true)
  }, [])

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingItem(null)
  }

  const handleOpenPublic = useCallback((job: IJobModel) => {
    if (job.id) window.open(publicJobUrl(job.id), '_blank', 'noopener')
  }, [])

  const handleDashboard = useCallback(
    (job: IJobModel) => {
      if (job.id) history.push(jobDashboardPath(job.id))
    },
    [history]
  )

  const handleSubmit = async (values: IJobFormValues) => {
    setSaving(true)
    try {
      const request = toJobRequest(values)
      if (editingItem?.id) {
        await updateJob(editingItem.id, request)
        showSuccessToast('İlan başarıyla güncellendi')
        await fetchJobs(currentPage)
      } else {
        await createJob(request)
        showSuccessToast('İlan başarıyla oluşturuldu')
        setCurrentPage(0)
        await fetchJobs(0)
      }
      handleCloseForm()
    } catch (error) {
      showErrorToast('Bir hata oluştu')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deletingItem?.id) return
    setLoading(true)
    try {
      await deleteJob(deletingItem.id)
      showSuccessToast('İlan başarıyla silindi')
      await fetchJobs(currentPage)
    } catch (error) {
      showErrorToast('İlan silinirken hata oluştu')
    } finally {
      setDeletingItem(null)
      setLoading(false)
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>İş İlanı Tanımı</PageTitle>

      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>İş İlanları</span>
            <span className='text-muted mt-1 fw-bold fs-7'>
              Yayındaki ilanlar herkese açık bağlantı ile paylaşılabilir
            </span>
          </h3>
          <div className='card-toolbar'>
            <button type='button' className='btn btn-sm btn-primary' onClick={handleCreateClick}>
              <i className='bi bi-plus-lg fs-5'></i>
              Yeni İlan
            </button>
          </div>
        </div>
        <div className='card-body pt-0'>
          <JobTable
            data={jobs}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onEdit={handleEdit}
            onDelete={setDeletingItem}
            onOpenPublic={handleOpenPublic}
            onShare={setSharingItem}
            onDashboard={handleDashboard}
            loading={loading}
          />
        </div>
      </div>

      <ShareJobModal
        job={sharingItem?.id ? {...sharingItem, id: sharingItem.id} : null}
        onClose={() => setSharingItem(null)}
      />

      <JobFormModal
        show={showForm}
        editingItem={editingItem}
        loading={saving}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
      />

      <ConfirmModal
        show={!!deletingItem}
        header='Silme Onayı'
        text={`"${deletingItem?.title ?? ''}" ilanını silmek istediğinize emin misiniz?`}
        handleClose={() => setDeletingItem(null)}
        applyOnClick={handleDelete}
      />

      <ToastContainer
        position='top-right'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
      />
    </>
  )
}

export {JobPage}
